import { useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { styled } from "@mui/material/styles";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import uniqBy from "lodash/uniqBy";

import { MyActionsList } from "components/MyActions/MyActionsList";
import { API_URL_MY_ACTIONS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { makeQueryString } from "core/makeQueryString";
import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useTrackPageView } from "hooks/useTrackPageView";
import { IApiError } from "interfaces/api-error";
import { CaseActionStatusType, MyActionListItem } from "interfaces/case-action";
import { ActionPanel } from "pages/MyActionsPage/ActionPanel";
import { CustomPrompt } from "components/CustomPrompt";
import { FeaturesContext } from "contexts/FeaturesContext";

const PAGE_TITLE = "My Actions";
const CONFIRM_NAVIGATION_MESSAGE =
  "You have unsaved changes in the Your Action form. Are you sure you want to navigate away?";

interface IPaginatedResponse<T> {
  count: number;
  results: T[];
}

interface FilterOption {
  label: string;
  value: string;
}

export const MyActionsPage = (): JSX.Element => {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);

  const { hasFeature } = useContext(FeaturesContext);
  const [selectedListTab, setSelectedListTab] = useState<number>(1);
  const [selectedActionId, setSelectedActionId] = useState<number | null>(null);
  const [
    hasUnsavedActionFormChanges,
    setHasUnsavedActionFormChanges
  ] = useState(false);
  const [actionFilterValue, setActionFilterValue] = useState<string>("all");

  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const handleAxiosError = (defaultErrorMessage: string) => (
    err: AxiosError<IApiError>
  ) => {
    addSnackbarToQueue({
      severity: "error",
      message: err?.response?.data?.error || defaultErrorMessage
    });
  };

  const actionFetchStatus = useMemo(() => {
    let status: CaseActionStatusType[] | undefined = undefined;

    /**
     * Tab label by number
     * 1: Open / In progress
     * 2: Submitted
     * 3: Closed
     * 4: All
     * */
    switch (selectedListTab) {
      case 1:
        status = [CaseActionStatusType.Open, CaseActionStatusType.InProgress];
        break;
      case 2:
        status = [CaseActionStatusType.InReview];
        break;
      case 3:
        status = [
          CaseActionStatusType.Completed,
          CaseActionStatusType.Canceled
        ];
        break;
      case 4:
      default:
        break;
    }

    return status;
  }, [selectedListTab]);

  const myActionsQuery = useQuery({
    queryKey: ["my-actions", "All"],
    queryFn: () =>
      axios
        .get<IPaginatedResponse<MyActionListItem>>(
          `${API_URL_MY_ACTIONS}${makeQueryString({
            page: 1,
            size: 9999999,
            ordering: "requested_completion_date"
          })}`
        )
        .then(r => r.data?.results || []),
    onError: handleAxiosError("Error fetching My Actions."),
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  const myActionsByStatusQuery = useQuery({
    queryKey: ["my-actions", { status: actionFetchStatus }],
    queryFn: () =>
      axios
        .get<IPaginatedResponse<MyActionListItem>>(
          `${API_URL_MY_ACTIONS}${makeQueryString({
            page: 1,
            size: 9999999,
            action_status__in: actionFetchStatus,
            ordering: "requested_completion_date"
          })}`
        )
        .then(r => r.data?.results || []),
    onError: handleAxiosError("Error fetching My Actions by Status."),
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  const filterOptions: FilterOption[] = useMemo(() => {
    return uniqBy(myActionsQuery.data ?? [], "project_id")
      .filter(item => !!item.project_id || !!item.project_name)
      .map(item => ({
        label: `${item.project_name}`,
        value: String(item.project_id)
      }))
      .sort((a, b) => {
        if (a.label && b.label) {
          return a.label.localeCompare(b.label);
        }
        return 0;
      });
  }, [myActionsQuery.data]);

  const actionsToDisplay: MyActionListItem[] = useMemo(() => {
    if (!hasFeature("projects") || actionFilterValue === "all") {
      return myActionsByStatusQuery.data ?? [];
    }

    if (actionFilterValue === "non-project") {
      return (myActionsByStatusQuery.data ?? []).filter(
        (action: MyActionListItem) => !action.project_id
      );
    }

    const projectId = Number(actionFilterValue);
    return (myActionsByStatusQuery.data ?? []).filter(
      (action: MyActionListItem) => action.project_id === projectId
    );
  }, [actionFilterValue, hasFeature, myActionsByStatusQuery.data]);

  useEffect(() => {
    setSelectedActionId(prevActionId => {
      if (prevActionId === null) {
        return (
          myActionsByStatusQuery?.data?.find(action => !!action)?.id ?? null
        );
      }
      return prevActionId;
    });
  }, [myActionsByStatusQuery?.data]);

  const handleChangeSelectedActionId = (actionId: number): void => {
    if (actionId !== selectedActionId && hasUnsavedActionFormChanges) {
      const confirmNavigation = window.confirm(CONFIRM_NAVIGATION_MESSAGE);

      if (!confirmNavigation) {
        return;
      }
    }
    setSelectedActionId(actionId);
  };

  return (
    <>
      <CustomPrompt
        shouldBlockNavigation={hasUnsavedActionFormChanges}
        content={CONFIRM_NAVIGATION_MESSAGE}
      />
      <CssGrid>
        <PaddedPaper>
          <Typography variant="h4" component="h1">
            Your Actions
          </Typography>

          {hasFeature("projects") && (
            <FormControl fullWidth>
              <InputLabel id="actions-filter">Filter</InputLabel>
              <Select
                labelId="actions-filter"
                id="actions-filter-select"
                value={actionFilterValue}
                label="Filter"
                onChange={event => setActionFilterValue(event.target.value)}
                size="small"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="non-project">Other Actions</MenuItem>
                {filterOptions.map(option => (
                  <MenuItem value={option.value} key={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <MyActionsList
            actions={actionsToDisplay}
            loading={myActionsByStatusQuery?.isLoading}
            error={myActionsByStatusQuery?.isError}
            selectedListTab={selectedListTab}
            selectedActionId={selectedActionId}
            onChangeSelectedListTab={setSelectedListTab}
            onChangeSelectedActionId={handleChangeSelectedActionId}
          />
        </PaddedPaper>
        {selectedActionId ? (
          <ActionPanel
            selectedActionId={selectedActionId}
            onFormChange={setHasUnsavedActionFormChanges}
          />
        ) : (
          <NoDataTextContainer>Nothing to show</NoDataTextContainer>
        )}
      </CssGrid>
    </>
  );
};

const CssGrid = styled("div")`
  display: grid;
  height: calc(100vh - 100px);
  gap: ${({ theme }) => theme.spacing(2)};

  ${props => props.theme.breakpoints.down("sm")} {
    grid-template-columns: auto;
    grid-template-rows: auto;
    grid-template-areas:
      "s1"
      "s2";
  }
  ${props => props.theme.breakpoints.up("sm")} {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "s1 s2";
  }
  ${props => props.theme.breakpoints.up("md")} {
    grid-template-columns: 380px 1fr;
    grid-template-areas: "s1 s2";
  }
`;

const PaddedPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
`;
PaddedPaper.displayName = "PaddedPaper";

const NoDataTextContainer = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  font-size: 24px;
  font-weight: 600;
`;
NoDataTextContainer.displayName = "NoDataTextContainer";
